import React from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { constants, currencyString } from "@qgiv/core-js";
import { ThemeType } from "../../../redux/constants";
import { selectReceipt } from "../../../redux/slices/receiptSlice";
import { selectAllP2pSettings } from "../../../redux/slices/p2pSettingsSlice";
import { selectAllFormSettings } from "../../../redux/slices/formSettingsSlice";
import { selectThemeSettings } from "../../../redux/slices/cmsSettingsSlice";
import ReceiptDonation from "./ReceiptDonation";

const ConnectedReceiptDonation = ({ id /* references donation id */ }) => {
    const {
        ENUMS: { ActivityRole, EntityType },
    } = constants;
    const themeSettings = useSelector((state) =>
        selectThemeSettings(ThemeType.SUMMARY, state),
    );
    const {
        p2pGifts: {
            optionalGiftLabel = "Gift toward your goal",
            optionalGiftLabelForNonFundraisingParticipants = "Gift toward %Entity%'s goal",
        },
    } = themeSettings;

    const receipt = useSelector(selectReceipt);
    const formSettings = useSelector(selectAllFormSettings);
    const p2pSettings = useSelector(selectAllP2pSettings);
    const { enableClassificationForDonation } = p2pSettings;

    const { currency, title } = formSettings;

    const {
        transaction: { donations = [], registrations = [] },
    } = receipt;

    const donation = donations.find((_donation) => _donation.id === id) || {};

    const { entityType, entity } = donation;

    const mainParticipant = registrations.length > 0 ? registrations[0] : {};

    const { teamName = "", classificationName = "" } = mainParticipant;

    const parentRegistrationName = Object.keys(mainParticipant).includes(
        "parentName",
    )
        ? mainParticipant.parentName
        : "";

    // As there is no isNfp value present in the registration data on the
    // receipt page activityRole can be used to indicate whether or not a user
    // is a fundraising participant
    const isNfp = !!(
        Object.keys(mainParticipant).includes("activityRole") &&
        mainParticipant.activityRole === ActivityRole.NONFUNDRAISING
    );

    const isPrimaryRegistrant = !!(
        Object.keys(mainParticipant).includes("parentRegistration") &&
        Number(mainParticipant.parentRegistration) === 0
    );

    const createEntityReplaceForReceipt = () => {
        // Uses a combination of entityTypes and entity id's to create correct
        // entity replace text
        switch (entityType) {
            case EntityType.TEAM:
                return teamName;
            // TYPE REGISTRATION only matters during fundraising center registration
            case EntityType.REGISTRATION:
                // Entity donated to is the main registration id
                // This happens during fundraising center registration when added participant
                // is a fundraising participant
                if (entity === mainParticipant.id) {
                    return `${mainParticipant.firstName} ${mainParticipant.lastName}`;
                }
                // Entity donated to is the parent registration's goal
                // This happens during fund center registration when added participant
                // is a non-fundraising participant
                if (parentRegistrationName) {
                    return parentRegistrationName;
                }
                return title;
            case EntityType.FORM:
            case EntityType.EVENT:
                // Check for a classification name and the event level setting
                // that allows donations to be attributed to a classification
                if (
                    classificationName.length > 0 &&
                    enableClassificationForDonation
                ) {
                    return classificationName;
                }
                return title;
            default:
                return title;
        }
    };

    const entityReplace = createEntityReplaceForReceipt();

    const labelWithEntityReplaced =
        optionalGiftLabelForNonFundraisingParticipants.replace(
            "%Entity%",
            entityReplace,
        );

    const donationLabel =
        isPrimaryRegistrant && !isNfp
            ? optionalGiftLabel
            : labelWithEntityReplaced;

    const amount = currencyString(donation.amount, currency);

    const receiptDonationProps = {
        ...donation,
        amount,
        donationLabel,
        isNfp,
    };

    return <ReceiptDonation {...receiptDonationProps} />;
};

ConnectedReceiptDonation.propTypes = {
    id: PropTypes.number.isRequired,
};

export default ConnectedReceiptDonation;
